import { useState, type FormEvent } from 'react'
import type { ChatMessageDto } from '../../types/community'
import { Card } from '../ui/Card'
import { Button } from '../ui/Button'
import { cn } from '../../lib/cn'

interface LobbyChatProps {
  messages: ChatMessageDto[]
  onSend: (body: string) => void | boolean | Promise<void | boolean>
  sending?: boolean
  /** View-only profiles cannot send — the composer is replaced with a note. */
  canSend?: boolean
  readOnlyReason?: string
}

function MessageRow({ m }: { m: ChatMessageDto }) {
  return (
    <div
      className={cn(
        'rounded-[10px] px-3 py-2',
        m.isSystem
          ? 'bg-[#f2f8f3]'
          : m.isSelf
            ? 'bg-paper-panel'
            : 'bg-paper-surface',
      )}
    >
      <div className="flex items-baseline gap-2">
        <span
          className={cn(
            'font-mono text-[12px] font-medium',
            m.isSystem ? 'text-moss' : 'text-ink',
          )}
        >
          <span aria-hidden className="mr-1">{m.glyph}</span>
          {m.authorFirstName}
        </span>
        <span className="font-mono text-[10px] uppercase tracking-label text-ink-fainter">{m.whenText}</span>
      </div>
      <p className="mt-1 text-[13.5px] leading-relaxed text-ink">{m.body}</p>
    </div>
  )
}

/** Filtered lobby chat panel (COTC_Rooms R3 / DESIGN §3.5). */
export function LobbyChat({ messages, onSend, sending, canSend = true, readOnlyReason }: LobbyChatProps) {
  const [draft, setDraft] = useState('')

  async function submit(e: FormEvent) {
    e.preventDefault()
    const body = draft.trim()
    if (!body || sending) return
    const accepted = await onSend(body)
    if (accepted !== false) setDraft(current => current.trim() === body ? '' : current)
  }

  return (
    <Card padding="none" className="flex min-h-[520px] flex-col overflow-hidden">
      <div className="border-b border-line-row px-5 py-3">
        <span className="font-mono text-[11px] uppercase tracking-label text-moss">
          Filtered · a moderator can read this
        </span>
      </div>

      <div className="flex-1 space-y-2 overflow-y-auto px-5 py-4">
        {messages.length === 0 ? (
          <p className="py-6 text-center text-[13px] text-ink-muted">
            No messages yet. Say something kind or useful to get things going.
          </p>
        ) : (
          messages.map((m) => <MessageRow key={m.id} m={m} />)
        )}
      </div>

      <div className="border-t border-line-row px-5 py-4">
        {canSend ? (
          <form onSubmit={submit} className="flex items-center gap-2">
            <input
              aria-label="Message"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder="Say something kind or useful…"
              maxLength={500}
              className="min-w-0 flex-1 rounded-input border border-line bg-paper-surface px-[14px] py-3 text-[14px] font-sans text-ink outline-none placeholder:text-ink-fainter focus:border-terra"
            />
            <Button type="submit" variant="ink" disabled={sending || !draft.trim()}>
              Send
            </Button>
          </form>
        ) : (
          <p className="text-[13px] text-ink-muted">
            {readOnlyReason || 'You can read this room, but a grown-up sends messages for you.'}
          </p>
        )}
        <p className="mt-2 font-mono text-[11px] text-ink-faint">
          Phone numbers, schools and off-site handles are held for review automatically.
        </p>
      </div>
    </Card>
  )
}
